/**
 * Form hook for EditSentDropModal – manages all state, callbacks, and computed values.
 */

import { useEditSentDropBaseState } from './useEditSentDropBaseState';
import { useEditSentDropListsAndTemplate } from './useEditSentDropListsAndTemplate';
import { useEditSentDropPickers } from './useEditSentDropPickers';
import { useEditSentDropSave } from './useEditSentDropSave';
import type { EditSentDropFormParams } from './EditSentDropModal.types';

export function useEditSentDropForm(params: EditSentDropFormParams) {
  const {
    countryKey,
    drops,
    wiserId,
    senderNames,
    smsTexts,
    onClose,
    onAddSender,
    onAddText,
    onDeleteText,
    onDeleteTextsBulk,
    onSave,
    initialApplyPayload,
    onInitialApplyConsumed,
  } = params;

  const base = useEditSentDropBaseState({ drops, initialApplyPayload, onInitialApplyConsumed });
  const {
    drop,
    isBulk,
    routeId,
    setRouteId,
    tldValue,
    setTldValue,
    title,
    setTitle,
    selectedSenderNames,
    setSelectedSenderNames,
    selectedSmsTexts,
    setSelectedSmsTexts,
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    localSenders,
    setLocalSenders,
    localTexts,
    setLocalTexts,
    forceSend,
    setForceSend,
    useOnlyNewDomains,
    setUseOnlyNewDomains,
    clickIdStructure,
    setClickIdStructure,
    domainScheme,
    setDomainScheme,
    creativeAi,
  } = base;

  const {
    sendersList,
    textsList,
    effectiveRouteIdForTemplates,
    senderTemplates,
    activeTemplateId,
    handleSelectTemplate,
    handleSaveAsTemplate,
  } = useEditSentDropListsAndTemplate({
    drops,
    countryKey,
    routeId,
    senderNames,
    smsTexts,
    localSenders,
    localTexts,
    selectedSenderNames,
    setSelectedSenderNames,
    onAddSender,
  });

  const pickers = useEditSentDropPickers({
    sendersList,
    textsList,
    setSelectedSenderNames,
    setSelectedSmsTexts,
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    setLocalSenders,
    setLocalTexts,
    onAddSender,
    onAddText,
    onDeleteText,
    onDeleteTextsBulk,
    creativeAi,
  });

  const save = useEditSentDropSave({
    drops,
    isBulk,
    wiserId,
    title,
    routeId,
    tldValue,
    forceSend,
    clickIdStructure,
    domainScheme,
    selectedSenderNames,
    selectedSmsTexts,
    onSave,
    onClose,
    creativeAi,
  });

  return {
    drop,
    isBulk,
    routeId,
    setRouteId,
    tldValue,
    setTldValue,
    title,
    setTitle,
    selectedSenderNames,
    selectedSmsTexts,
    sendersList,
    textsList,
    senderTemplates,
    activeTemplateId,
    handleSelectTemplate,
    handleSaveAsTemplate,
    effectiveRouteIdForTemplates,
    forceSend,
    setForceSend,
    useOnlyNewDomains,
    setUseOnlyNewDomains,
    /** Shaped for DropOptionsSection's `shortlink` prop. */
    shortlink: { clickIdStructure, setClickIdStructure, domainScheme, setDomainScheme },
    ...creativeAi,
    ...pickers,
    ...save,
  };
}
